/* External */
import {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useParams } from 'react-router-dom';

/* Utils */
import { useOnBackPress, useOnKeyDown } from '../../utils/keyDownEvents';
import { history } from '../../utils/history';
import { TERMS_AND_CONDITIONS_PAGE, SCROLL_TO_MOVE, PRIVACY_POLICY_PAGE } from './constants';
import platform from '../../utils/platform';
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../utils/googleTags';

/* Providers */
import { useLoading } from '../../providers/LoadingProvider';
import { useTermsOfServiceInfo } from '../../providers/TermsOfServiceProvider';

/* Styles */
import './TermsOfService.scss';
import { useCountry } from '../../providers/ConfigProvider';

const NAVIGATION_KEY_CODES = [platform.keyCodes.navigation.up, platform.keyCodes.navigation.down];

function TermsOfService() {
  const [isScrolled, setIsScrolled] = useState(false);
  const contentTermsOfService = useRef();
  const scrollTopContent = useRef(0);
  const { page } = useParams();
  const { hideLoading } = useLoading();
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);

  const { termsOfService } = useTermsOfServiceInfo();

  const textToRender = useMemo(() => {
    if (termsOfService) {
      const { privacyPolicyItems, termsAndConditionsItems } = termsOfService;
      if (page === TERMS_AND_CONDITIONS_PAGE) {
        return termsAndConditionsItems[0];
      }
      return privacyPolicyItems[0];
    }
    return {};
  }, [termsOfService, page]);

  const { title, subtitle, content } = textToRender;

  const innerHtml = useMemo(() => ({ __html: content }), [content]);

  const onBackPress = useCallback(() => {
    history.goBack();
  }, []);

  useOnBackPress({ listener: onBackPress });

  history.listen((location, action) => {
    if (action === 'POP') {
      history.replace(location.pathname, { reviewedAgreement: page });
    }
  });

  const handleManuallyScroll = useCallback(
    ({ keyCode }) => {
      contentTermsOfService.current.scrollTop = keyCode === platform.keyCodes.navigation.down
        ? scrollTopContent.current + SCROLL_TO_MOVE
        : scrollTopContent.current - SCROLL_TO_MOVE;
    },
    [],
  );

  useOnKeyDown({ listener: handleManuallyScroll, forKeyCodes: NAVIGATION_KEY_CODES });

  const handleOnScroll = useCallback(
    (event) => {
      scrollTopContent.current = event.target.scrollTop;
      setIsScrolled(event.target.scrollTop > 5);
    },
    [],
  );

  useEffect(() => {
    if (termsOfService) {
      hideLoading();
    }
  }, [termsOfService, hideLoading]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      if (page === TERMS_AND_CONDITIONS_PAGE) {
        commonTaggingEvent({
          category: GTM_CATEGORIES.SETTINGS_DATA,
          subCategory: GTM_SUBCATEGORIES.SETTINGS.TERMS_AND_CONDITIONS,
          eventName: GTM_EVENT_NAMES.PAGE_VIEW,
        });
      } else if (page === PRIVACY_POLICY_PAGE) {
        commonTaggingEvent({
          category: GTM_CATEGORIES.SETTINGS_DATA,
          subCategory: GTM_SUBCATEGORIES.SETTINGS.PRIVACY_POLICY,
          eventName: GTM_EVENT_NAMES.PAGE_VIEW,
        });
      }
      tagsMountRef.current = false;
    }
  }, [commonTaggingEvent, country, page]);

  return (
    <div className={`dtv-html5-c-terms-of-service ${isScrolled ? 'dtv-html5-c-terms-of-service--scrolled' : ''}`}>
      <h1 className="dtv-html5-c-terms-of-service__title">{title}</h1>
      <div className="dtv-html5-c-terms-of-service__content" onScroll={handleOnScroll} ref={contentTermsOfService}>
        <h2 className="dtv-html5-c-terms-of-service__subtitle">{subtitle}</h2>
        {content
          // eslint-disable-next-line react/no-danger -- We trust on the content
          && <div className="dtv-html5-c-terms-of-service__description" dangerouslySetInnerHTML={innerHtml} />}
      </div>
    </div>
  );
}

export default (memo(TermsOfService));
